import type {
  PublisherBrokenReason,
  PubSubStatusStats,
  SubscriberBrokenReason,
} from './types';
import { getPublisherBrokenReason } from './getPublisherBrokenReason';
import { getSubscriberBrokenReason } from './getSubscriberBrokenReason';
import { session, SessionConnectionStatus } from '../../../webrtc/session';

const DEFAULT_STATS: PubSubStatusStats = {
  psStatusPubPublisherMissing: 0,
  psStatusPubCameraSourceMissing: 0,
  psStatusPubGhostPublisher: 0,
  psStatusPubStreamMissing: 0,
  psStatusPubMultiplePublishers: 0,
  psStatusPubVideoDisabled: 0,
  psStatusPubVideoNotRendering: 0,
  psStatusPubVideoHasNoTrack: 0,
  psStatusPubVideoTrackMuted: 0,
  psStatusSubSubscriberMissing: 0,
  psStatusSubCameraSourceMissing: 0,
  psStatusSubMultipleSubscribes: 0,
  psStatusSubVideoDisabled: 0,
  psStatusSubVideoNotRendering: 0,
  psStatusSubVideoHasNoTrack: 0,
  psStatusSubVideoTrackDisabled: 0,
  psStatusSubVideoTrackMuted: 0,
};

/**
 * Collects stats on publisher / subscriber broken status.
 */
class PubSubStatusStatsCollector {
  private stats: PubSubStatusStats = { ...DEFAULT_STATS };

  /**
   * Every 3 seconds we are going to check if all publishers/subscribers work properly.
   */
  checkInterval = 3000;

  /**
   * If we have broken publisher/subscriber during 15 seconds - log it.
   */
  errorsCountThreshold = 5;

  private publisherErrors: PublisherBrokenReason[] = [];
  private subscriberErrors: SubscriberBrokenReason[] = [];
  interval: NodeJS.Timeout | undefined;

  async collect(): Promise<void> {
    /**
     * Clean up data before collecting. This will be called right when session has started.
     */
    this.stats = { ...DEFAULT_STATS };
    this.publisherErrors = [];
    this.subscriberErrors = [];

    clearInterval(this.interval);
    this.interval = setInterval(async () => {
      this.check();
    }, this.checkInterval);
  }

  finalizeAndGetStats(): PubSubStatusStats {
    clearInterval(this.interval);
    return this.stats;
  }

  private check(): void {
    /**
     * We are not going to track stats if we are not in a call but we still need to continue collecting data
     * when call has been put on hold and continued.
     */
    if (!this.isInCall()) {
      return;
    }

    this.checkBrokenStatus(getPublisherBrokenReason(), this.publisherErrors);

    this.checkBrokenStatus(getSubscriberBrokenReason(), this.subscriberErrors);
  }

  private checkBrokenStatus(
    brokenReason:
      | PublisherBrokenReason
      | SubscriberBrokenReason
      | undefined
      | null,
    errors: (PublisherBrokenReason | SubscriberBrokenReason)[],
  ): void {
    const isFirstOrSameReasonAsBefore = errors.at(-1)
      ? errors.at(-1) === brokenReason
      : true;

    /**
     * We are going to track the error if:
     * 1. We have an error
     * 2. We have the same error as before
     * It would help us avoid tracking some long transition processes that might end up with working publisher/subscriber.
     */
    if (brokenReason && isFirstOrSameReasonAsBefore) {
      errors.push(brokenReason);
      /**
       * If we have 5 same errors in a row - track it and start counting from the beginning.
       */
      if (errors.length === this.errorsCountThreshold) {
        this.stats[brokenReason as keyof PubSubStatusStats] +=
          this.getErrorDurationSeconds();
        errors.length = 0;
      }
    } else {
      /**
       * If no brokenReason or it is not the same as before - reset the counter.
       */
      errors.length = 0;
    }
  }

  private getErrorDurationSeconds(): number {
    return (this.checkInterval * this.errorsCountThreshold) / 1000;
  }

  private isInCall(): boolean {
    return session.connectionStatus === SessionConnectionStatus.Connected;
  }
}

export const pubSubStatusStats = new PubSubStatusStatsCollector();
